import { useCallback, useEffect, useMemo } from 'react';
import { getFulfillmentPlanTaxIdentifiers, postFulfillmentPlanTaxIdentifiers } from '../../../../services/tax-identifiers/api';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { LogService } from '../../../../services/log-service';

export const useFulfillmentPlanTaxIdentifiers = (fulfillmentPlanId) => {
  const [errors, setErrors] = useAsyncSafeState();
  const [loading, setLoading] = useAsyncSafeState(false);
  const [saving, setSaving] = useAsyncSafeState(false);
  const [selectedTaxIdentifierIds, setSelectedTaxIdentifierIds] = useAsyncSafeState([]);

  useEffect(() => {
    setLoading(true);
    setErrors(null);
    const fetch = async () => {
      try {
        const identifiers = await getFulfillmentPlanTaxIdentifiers(fulfillmentPlanId);
        setLoading(false);
        setSelectedTaxIdentifierIds(identifiers);
      } catch (error) {
        LogService.logError(new Error('Error fetching fulfillment plan tax identifiers', { cause: error }));
        setLoading(false);
        setErrors(error);
      }
    };
    fetch();
  }, [fulfillmentPlanId, setSelectedTaxIdentifierIds, setErrors, setLoading]);

  const updateFpTaxIdentifiers = useCallback(
    async (newSelectedTaxIdentifierIds) => {
      setSaving(true);
      setErrors(null);
      try {
        const identifiers = await postFulfillmentPlanTaxIdentifiers(fulfillmentPlanId, newSelectedTaxIdentifierIds);
        setSaving(false);
        setSelectedTaxIdentifierIds(identifiers);
      } catch (error) {
        LogService.logError(new Error('Error posting fulfillment plan tax identifiers', { cause: error }));
        setSaving(false);
        setErrors(error);
      }
    },
    [fulfillmentPlanId, setErrors, setSaving, setSelectedTaxIdentifierIds],
  );

  return useMemo(
    () => ({
      selectedTaxIdentifierIds,
      updateFpTaxIdentifiers,
      loading,
      saving,
      errors,
    }),
    [selectedTaxIdentifierIds, updateFpTaxIdentifiers, loading, saving, errors],
  );
};
